import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./InputField.module.scss');

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

type InputSize = 'S' | 'L';

const SIZE_MAP: Record<InputSize, string> = {
  S: 'small',
  L: 'large',
};

type UserType = 'talent' | 'company' | 'neutral';

export interface InputFieldProps extends NativeInputProps {
  autoSaveUrl?: string;
  containerClassName?: string;
  error?: string;
  hint?: string;
  iconLeft?: string;
  iconRight?: string;
  onIconRightClick?: () => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  size?: InputSize;
  context?: UserType;
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  autoSaveUrl,
  containerClassName,
  error,
  hint,
  iconLeft,
  iconRight,
  onIconRightClick,
  label,
  required,
  placeholder,
  size = 'S',
  context = 'neutral',
  inputRef,
  disabled,
  id,
  type = 'text',
  ...inputProps
}) => {
  const { autoSaveError, save } = useAutoSave({ url: autoSaveUrl || '', name: inputProps.name || '' });
  const [displayError, setDisplayError] = useState(error || '');
  useEffect(() => { setDisplayError(error || ''); }, [error]);
  const isControlled = inputProps.onChange !== undefined;

  useEffect(() => { if (autoSaveError) setDisplayError(autoSaveError); }, [autoSaveError]);
  const sizeClass = SIZE_MAP[size];
  const inputId = id || `input-field-${React.useId ? React.useId() : Math.random().toString(36).slice(2)}`;

  useEffect(() => {
    setDisplayError(error || '');
  }, [error]);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayError('');
    if (autoSaveUrl) save(e.target.value);
    if (inputProps.onChange) inputProps.onChange(e);
  };

  const showHint = Boolean(hint) && !displayError;
  const hasMessage = Boolean(displayError) || showHint;
  const messageId = hasMessage ? `${inputId}-message` : undefined;

  const inputContainerClasses = classNames(
    styles.inputContainer,
    {
      [styles[sizeClass]]: sizeClass,
      [styles.error]: Boolean(displayError),
      [styles.disabled]: disabled,
      [styles[context]]: context,
    }
  );

  const wrapperClasses = classNames(
    styles.wrapper,
    containerClassName
  );

  const { onChange: _onChange, value: nativeValue, ...safeInputProps } = inputProps;

  return (
    <div className={wrapperClasses}>
      {label && (
        <div className={styles.labelContainer}>
          <label htmlFor={inputId} className={styles.label}>{label}</label>
          {required && <span className={styles.required} aria-hidden="true">*</span>}
        </div>
      )}

      <div className={styles.inputWrapper}>
        <label htmlFor={inputId} className={inputContainerClasses}>
          {iconLeft && (
            <div className={styles.iconLeftContainer}>
              <Icon
                name={iconLeft}
                size="md"
                color='black'
                aria-hidden='true'
              />
            </div>
          )}

          <input
            id={inputId}
            type={type}
            className={styles.input}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            aria-invalid={Boolean(displayError)}
            aria-describedby={messageId}
            ref={inputRef}
            onChange={handleChange}
            {...safeInputProps}
            {...(!isControlled && nativeValue !== undefined
              ? { defaultValue: nativeValue }
              : isControlled ? { value: nativeValue } : {}
            )}
          />

          {iconRight && onIconRightClick ? (
            <button type="button" className={styles.iconRightContainer} onClick={onIconRightClick}>
              <Icon name={iconRight} size="md" color="black" aria-hidden="true" />
            </button>
          ) : iconRight ? (
            <div className={styles.iconRightContainer}>
              <Icon name={iconRight} size="md" color="black" aria-hidden="true" />
            </div>
          ) : null}
        </label>

        {hasMessage && (
          <div
            id={messageId}
            role={displayError ? 'alert' : undefined}
            className={classNames(styles.hintContainer, {
              [styles.hintError]: Boolean(displayError),
            })}
          >
            <Icon
              name={displayError ? 'triangleAlert' : 'info'}
              color={displayError ? 'error' : 'disable'}
              size="sm"
              aria-hidden='true'
            />
            <p className={styles.hintText}>{displayError || hint}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputField;
